import React, { useState, useEffect } from 'react';
import { X, Users, Plus, Minus, Check, UserPlus, Stethoscope } from 'lucide-react';
import { staffService } from '../utils/supabaseStaffService';
import { attendeeService } from '../services/attendeeService';
import { logger } from '../utils/logger';

const AttendeeManagementModal = ({
  isOpen,
  onClose,
  customerId,
  meetingId,
  initialSelectedStaff = [],
  onSave
}) => {
  const [staffMembers, setStaffMembers] = useState([]);
  const [physicians, setPhysicians] = useState([]);
  const [selectedStaffIds, setSelectedStaffIds] = useState(new Set(initialSelectedStaff));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showAddStaffForm, setShowAddStaffForm] = useState(false);
  const [selectAll, setSelectAll] = useState(false);

  // New staff form state
  const [newStaffData, setNewStaffData] = useState({
    name: '',
    role: '',
    email: '',
    phone: ''
  });

  // Load customer's staff members AND physicians from meeting_attendees
  useEffect(() => {
    const loadAttendees = async () => {
      if (!isOpen) return;

      try {
        setLoading(true);

        // Load physicians from meeting_attendees table (if exists)
        if (meetingId) {
          try {
            logger.debug('Loading meeting attendees', { meetingId });
            const attendees = await attendeeService.getMeetingAttendees(meetingId);

            // Filter to get only physicians (excluding sales rep)
            const physicianAttendees = (attendees || []).filter(
              attendee => attendee.attendee_type === 'physician'
            );
            setPhysicians(physicianAttendees);

            logger.debug('Loaded physician attendees', { count: physicianAttendees.length });
          } catch (attendeeError) {
            // If meeting_attendees query fails (old meeting), that's OK - just no physicians
            logger.warn('Could not load meeting attendees (likely old meeting)', {
              error: attendeeError.message,
              meetingId
            });
            setPhysicians([]);
          }
        }

        // Load staff members
        if (customerId) {
          logger.debug('Loading staff members for customer', { customerId });
          const staff = await staffService.getStaffForPhysician(customerId);
          setStaffMembers(staff || []);
          logger.debug('Loaded staff members', { count: staff?.length || 0 });
        }
      } catch (error) {
        logger.error('Error loading attendees', { error: error.message });
        setStaffMembers([]);
        setPhysicians([]);
      } finally {
        setLoading(false);
      }
    };

    loadAttendees();
  }, [customerId, meetingId, isOpen]);

  // Update selectAll state when staff or selection changes
  useEffect(() => {
    if (staffMembers.length > 0) {
      setSelectAll(selectedStaffIds.size === staffMembers.length);
    }
  }, [staffMembers, selectedStaffIds]);

  const handleToggleStaff = (staffId) => {
    setSelectedStaffIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(staffId)) {
        newSet.delete(staffId);
      } else {
        newSet.add(staffId);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    if (selectAll) {
      // Deselect all
      setSelectedStaffIds(new Set());
    } else {
      // Select all
      setSelectedStaffIds(new Set(staffMembers.map(s => s.id)));
    }
  };

  const handleAddNewStaff = async () => {
    if (!newStaffData.name.trim()) {
      alert('Please enter a staff member name');
      return;
    }

    try {
      logger.debug('Adding new staff member', { customerId, staffData: newStaffData });

      const newStaff = await staffService.addStaffMember(customerId, {
        name: newStaffData.name.trim(),
        role: newStaffData.role.trim() || 'Team Member',
        email: newStaffData.email.trim() || null,
        phone: newStaffData.phone.trim() || null
      });

      if (newStaff) {
        logger.debug('Successfully added new staff member', { staffId: newStaff.id });

        // Add to staff list
        setStaffMembers(prev => [...prev, newStaff]);

        // Automatically select the new staff member
        setSelectedStaffIds(prev => {
          const newSet = new Set(prev);
          newSet.add(newStaff.id);
          return newSet;
        });

        // Reset form
        setNewStaffData({
          name: '',
          role: '',
          email: '',
          phone: ''
        });
        setShowAddStaffForm(false);
      } else {
        throw new Error('Failed to add staff member');
      }
    } catch (error) {
      logger.error('Error adding staff member', { error: error.message });
      alert('Error adding staff member. Please try again.');
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);

      const selectedStaff = staffMembers.filter(s => selectedStaffIds.has(s.id));
      // Calculate total: all physicians + selected staff + 1 sales rep
      const attendeeCount = physicians.length + selectedStaffIds.size + 1;

      logger.debug('Saving attendees', {
        meetingId,
        attendeeCount,
        physicianCount: physicians.length,
        selectedStaffIds: Array.from(selectedStaffIds)
      });

      await onSave(attendeeCount, selectedStaff);

      onClose();
    } catch (error) {
      logger.error('Error saving attendees', { error: error.message });
      alert('Error saving attendees. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <Users className="w-6 h-6 text-blue-600" />
            <div>
              <h2 className="text-xl font-bold text-gray-800">Manage Meeting Attendees</h2>
              <p className="text-sm text-gray-600 mt-1">
                Select office staff who will attend this meeting
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <p className="text-gray-600">Loading staff members...</p>
              </div>
            </div>
          ) : (
            <>
              {/* Physicians Section (Read-only) */}
              {physicians.length > 0 && (
                <div className="mb-6">
                  <p className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                    <Stethoscope className="w-4 h-4 text-blue-600" />
                    Healthcare Providers ({physicians.length})
                  </p>
                  <div className="space-y-2">
                    {physicians.map((physician) => (
                      <div
                        key={physician.id}
                        className="flex items-center justify-between p-4 rounded-lg border-2 border-blue-200 bg-blue-50"
                      >
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 rounded-full bg-blue-600 text-white flex items-center justify-center">
                            <Stethoscope className="w-5 h-5" />
                          </div>
                          <div>
                            <p className="font-semibold text-gray-800">
                              {physician.user_profiles?.first_name} {physician.user_profiles?.last_name}
                            </p>
                            <p className="text-sm text-gray-600">
                              {physician.user_profiles?.specialty || 'Physician'}
                            </p>
                            {physician.user_profiles?.email && (
                              <p className="text-xs text-gray-500 mt-1">
                                {physician.user_profiles.email}
                              </p>
                            )}
                            {physician.is_organizer && (
                              <span className="inline-block mt-1 px-2 py-0.5 bg-blue-200 text-blue-800 text-xs font-medium rounded">
                                Organizer
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center space-x-2 text-blue-600">
                          <Check className="w-5 h-5" />
                          <span className="text-sm font-medium">
                            {physician.rsvp_status === 'accepted' ? 'Accepted' :
                             physician.rsvp_status === 'declined' ? 'Declined' : 'Pending'}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-3 p-2 bg-blue-50 border border-blue-200 rounded text-xs text-blue-700">
                    Healthcare providers cannot be removed from this meeting
                  </div>
                </div>
              )}

              {/* Select All Toggle */}
              {staffMembers.length > 0 && (
                <div className="mb-4 pb-4 border-b border-gray-200">
                  <button
                    onClick={handleSelectAll}
                    className={`
                      w-full flex items-center justify-between p-4 rounded-lg border-2 cursor-pointer transition-all
                      ${selectAll
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 bg-white hover:border-gray-300'
                      }
                    `}
                  >
                    <div className="flex items-center space-x-3">
                      <div className={`
                        w-10 h-10 rounded-full flex items-center justify-center
                        ${selectAll
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-200 text-gray-600'
                        }
                      `}>
                        {selectAll ? (
                          <Check className="w-5 h-5" />
                        ) : (
                          <Users className="w-5 h-5" />
                        )}
                      </div>
                      <div className="text-left">
                        <p className="font-semibold text-gray-800">
                          {selectAll ? 'Deselect All' : 'Select All'}
                        </p>
                        <p className="text-sm text-gray-600">
                          {staffMembers.length} staff member{staffMembers.length !== 1 ? 's' : ''}
                        </p>
                      </div>
                    </div>
                  </button>
                </div>
              )}

              {/* Staff List */}
              <div className="space-y-3 mb-6">
                {staffMembers.length === 0 ? (
                  <div className="text-center py-8 bg-gray-50 rounded-lg">
                    <Users className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                    <p className="text-gray-600 mb-2">No staff members found</p>
                    <p className="text-sm text-gray-500">Add staff members to include them in meetings</p>
                  </div>
                ) : (
                  <>
                    <p className="text-sm font-medium text-gray-700 mb-3">
                      Office Staff ({selectedStaffIds.size} of {staffMembers.length} selected)
                    </p>
                    {staffMembers.map((staff) => (
                      <div
                        key={staff.id}
                        onClick={() => handleToggleStaff(staff.id)}
                        className={`
                          flex items-center justify-between p-4 rounded-lg border-2 cursor-pointer transition-all
                          ${selectedStaffIds.has(staff.id)
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-200 bg-white hover:border-gray-300'
                          }
                        `}
                      >
                        <div className="flex items-center space-x-3">
                          <div className={`
                            w-10 h-10 rounded-full flex items-center justify-center
                            ${selectedStaffIds.has(staff.id)
                              ? 'bg-blue-600 text-white'
                              : 'bg-gray-200 text-gray-600'
                            }
                          `}>
                            {selectedStaffIds.has(staff.id) ? (
                              <Check className="w-5 h-5" />
                            ) : (
                              <Users className="w-5 h-5" />
                            )}
                          </div>
                          <div>
                            <p className="font-semibold text-gray-800">{staff.name}</p>
                            <p className="text-sm text-gray-600">{staff.role || 'Team Member'}</p>
                            {staff.email && (
                              <p className="text-xs text-gray-500 mt-1">{staff.email}</p>
                            )}
                          </div>
                        </div>

                        {selectedStaffIds.has(staff.id) && (
                          <div className="flex items-center space-x-2 text-blue-600">
                            <Check className="w-5 h-5" />
                            <span className="text-sm font-medium">Selected</span>
                          </div>
                        )}
                      </div>
                    ))}
                  </>
                )}
              </div>

              {/* Add Staff Member Form */}
              {showAddStaffForm ? (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">Add New Staff Member</h3>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={newStaffData.name}
                        onChange={(e) => setNewStaffData({ ...newStaffData, name: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="e.g., Jane Smith"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Role
                      </label>
                      <input
                        type="text"
                        value={newStaffData.role}
                        onChange={(e) => setNewStaffData({ ...newStaffData, role: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="e.g., Medical Assistant, Nurse"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Email
                        </label>
                        <input
                          type="email"
                          value={newStaffData.email}
                          onChange={(e) => setNewStaffData({ ...newStaffData, email: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="email@example.com"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Phone
                        </label>
                        <input
                          type="tel"
                          value={newStaffData.phone}
                          onChange={(e) => setNewStaffData({ ...newStaffData, phone: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="(555) 123-4567"
                        />
                      </div>
                    </div>
                    <div className="flex space-x-2 pt-2">
                      <button
                        onClick={handleAddNewStaff}
                        className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
                      >
                        Add Staff Member
                      </button>
                      <button
                        onClick={() => {
                          setShowAddStaffForm(false);
                          setNewStaffData({ name: '', role: '', email: '', phone: '' });
                        }}
                        className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-700 px-4 py-2 rounded-lg transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => setShowAddStaffForm(true)}
                  className="w-full flex items-center justify-center space-x-2 px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-blue-500 hover:text-blue-600 transition-colors"
                >
                  <UserPlus className="w-5 h-5" />
                  <span className="font-medium">Add New Staff Member</span>
                </button>
              )}

              {/* Summary */}
              <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-700">Total Attendees</p>
                    <p className="text-xs text-gray-500 mt-1">
                      {physicians.length} physician{physicians.length !== 1 ? 's' : ''} + {selectedStaffIds.size} staff + 1 sales rep
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-blue-600">{physicians.length + selectedStaffIds.size + 1}</p>
                    <p className="text-xs text-gray-500">people</p>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            disabled={saving}
            className="px-6 py-2 bg-gray-200 hover:bg-gray-300 disabled:opacity-50 text-gray-700 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || loading}
            className="px-6 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-lg flex items-center space-x-2 transition-colors"
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span>Saving...</span>
              </>
            ) : (
              <>
                <Check className="w-4 h-4" />
                <span>Save Attendees</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AttendeeManagementModal;
